import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule, Routes } from '@angular/router';
import { ShellComponent } from './layout/shell.component';
import { HomeComponent } from './pages/home/home.component';
import { ReserveComponent } from './pages/reserve/reserve.component';
import { InfoComponent } from './pages/info/info.component';
import { LoginComponent } from './pages/login/login.component';
import { RegisterComponent } from './pages/register/register.component';
import { ConfirmComponent } from './pages/confirm/confirm.component';
import { AdminGuard } from './guards/admin.guard';
import { MyReservationsComponent } from './pages/my-reservations/my-reservations.component';
import { DatePickerComponent } from './shared/date-picker/date-picker.component';
import { ClassListComponent } from './shared/class-list-admin/class-list.component';
import { PasswordResetComponent } from './pages/password-reset/password-reset.component';
import { AuthGuard } from './guards/auth.guard';
import { GuestGuard } from './guards/guest.guard';
import { PrivacybeleidComponent } from './pages/privacybeleid/privacybeleid.component';
import { MyDataComponent } from './pages/my-data/my-data.component';

const routerOptions: ExtraOptions = {
  anchorScrolling: 'enabled',
};

const routes: Routes = [
  {
    path: '',
    component: ShellComponent,
    children: [
      { path: '', redirectTo: '/start', pathMatch: 'full' },
      {
        path: 'start',
        component: HomeComponent,
        data: { breadcrumb: 'Start' },
      },
      {
        path: 'reserveer',
        component: ReserveComponent,
        title: 'Reserveer',
        data: { breadcrumb: 'Reserveer', noindex: true },
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            redirectTo: 'date',
            pathMatch: 'full',
          },
          {
            path: 'date',
            component: DatePickerComponent,
          },
          {
            path: 'class',
            component: ClassListComponent,
          },
        ],
      },
      {
        path: 'mijn-reservaties',
        component: MyReservationsComponent,
        title: 'Mijn reservaties',
        data: { breadcrumb: 'Mijn Reservaties', noindex: true },
        canActivate: [AuthGuard],
      },
      {
        path: 'mijn-gegevens',
        component: MyDataComponent,
        title: 'Mijn gegevens',
        data: { breadcrumb: 'Mijn gegevens', noindex: true },
        canActivate: [AuthGuard],
      },
      {
        path: 'info',
        component: InfoComponent,
        title: 'Uren, tarieven & lesgevers',
        data: {
          breadcrumb: 'Info',
          description:
            'Spinning bij Heracles Westerlo: lessen op maandag, dinsdag, woensdag en donderdag. ' +
            'Vanaf €8 per beurt. Bekijk de uren, tarieven en lesgevers.',
        },
      },
      {
        path: 'privacybeleid',
        component: PrivacybeleidComponent,
        title: 'Privacybeleid',
        data: {
          breadcrumb: 'Privacybeleid',
          description:
            'Privacyverklaring van spinningwesterlo.be: welke gegevens we verwerken en waarom.',
        },
      },
      {
        path: 'login',
        component: LoginComponent,
        title: 'Inloggen',
        data: { breadcrumb: 'Inloggen', noindex: true },
        canActivate: [GuestGuard],
      },
      {
        path: 'aanmelden',
        component: RegisterComponent,
        title: 'Aanmelden',
        data: {
          breadcrumb: 'Aanmelden',
          description:
            'Maak je account aan bij Spinning Heracles Westerlo en reserveer je spinninglessen online.',
        },
        canActivate: [GuestGuard],
      },
      {
        path: 'proefles',
        component: RegisterComponent,
        title: 'Gratis proefles',
        data: {
          breadcrumb: 'Proefles',
          trial: true,
          description:
            'Probeer spinning gratis uit bij Heracles Westerlo. Maak een account aan en boek je gratis proefles online.',
        },
        canActivate: [GuestGuard],
      },
      // The four admin screens are lazy: two club officials use them, every member and
      // every anonymous visitor was downloading them. They are the only routes behind
      // AdminGuard, so nothing else pays for the split.
      {
        path: 'admin',
        loadComponent: () =>
          import('./pages/admin/admin/admin.component').then(
            (m) => m.AdminComponent,
          ),
        title: 'Beheer',
        data: { breadcrumb: 'Beheer', noindex: true },
        canActivate: [AdminGuard],
      },
      {
        path: 'overzicht',
        loadComponent: () =>
          import('./pages/admin/overview/admin-overview.component').then(
            (m) => m.AdminOverviewComponent,
          ),
        title: 'Overzicht',
        data: { breadcrumb: 'Overzicht', noindex: true },
        canActivate: [AdminGuard],
      },
      {
        path: 'les-aanmaken',
        loadComponent: () =>
          import('./pages/admin/create-class/create-class.component').then(
            (m) => m.CreateClassComponent,
          ),
        title: 'Nieuwe les',
        data: { breadcrumb: 'Nieuwe les', noindex: true },
        canActivate: [AdminGuard],
      },
      {
        path: 'chip-toevoegen',
        loadComponent: () =>
          import('./pages/admin/add-chip-id/add-chip-id.component').then(
            (m) => m.AddChipIdComponent,
          ),
        title: 'Nieuwe chip',
        data: { breadcrumb: 'Nieuwe chip', noindex: true },
        canActivate: [AdminGuard],
      },
      {
        path: 'paswoord-vergeten',
        component: PasswordResetComponent,
        title: 'Wachtwoord vergeten',
        data: { breadcrumb: 'Wachtwoord vergeten', noindex: true },
      },
      {
        path: 'auth',
        data: { noindex: true },
        children: [
          {
            path: 'email/action',
            component: ConfirmComponent,
          },
        ],
      },
      { path: '**', redirectTo: '/start' },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
